import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { clearAuthState, setAuthState } from "./authSlice";

export type DeletionStatus = {
  enabled: boolean;
  delayDays: number;
  state: "none" | "pending" | "waiting" | "completed";
  requestedAt: string | null;
  scheduledFor: string | null;
  completedAt: string | null;
};

export type PrivacyInfo = {
  aiDisclosure: { version: string; provider: string; text: string };
  aiConsent: { granted: boolean; version: string; updatedAt: string | null };
  deletion: DeletionStatus;
};

type PrivacyState = {
  ownerId: string | null;
  info: PrivacyInfo | null;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;
  requestId: string | null;
};

const initialState: PrivacyState = {
  ownerId: null, info: null, loading: false, saving: false, deleting: false, error: null, requestId: null,
};

export const loadPrivacy = createAsyncThunk<PrivacyInfo>("privacy/load", async () =>
  request(API_URLS.accountPrivacy, { auth: true }));

export const saveAIConsent = createAsyncThunk<PrivacyInfo, { granted: boolean; version: string }>(
  "privacy/consent", async (body) => request(API_URLS.accountAIConsent, { method: "PUT", auth: true, body }),
);

export const requestAccountDeletion = createAsyncThunk<DeletionStatus, string>(
  "privacy/delete", async (confirmation) => request(API_URLS.accountDeletion, { method: "POST", auth: true, body: { confirmation } }),
);

const privacySlice = createSlice({
  name: "privacy",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(clearAuthState, () => initialState)
      .addCase(setAuthState, (state, action) => {
        const ownerId = action.payload?.user?.sub ?? null;
        if (state.ownerId !== ownerId) return { ...initialState, ownerId };
      })
      .addCase(loadPrivacy.pending, (state, action) => {
        state.loading = true; state.error = null; state.requestId = action.meta.requestId;
      })
      .addCase(loadPrivacy.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.info = action.payload; state.loading = false; state.requestId = null;
      })
      .addCase(loadPrivacy.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false; state.error = action.error.message || "Unable to load privacy choices."; state.requestId = null;
      })
      .addCase(saveAIConsent.pending, (state, action) => {
        state.saving = true; state.loading = false; state.error = null; state.requestId = action.meta.requestId;
      })
      .addCase(saveAIConsent.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.info = action.payload; state.saving = false; state.requestId = null;
      })
      .addCase(saveAIConsent.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.saving = false; state.error = action.error.message || "Unable to save permission."; state.requestId = null;
      })
      .addCase(requestAccountDeletion.pending, (state, action) => {
        state.deleting = true; state.error = null; state.requestId = action.meta.requestId;
      })
      .addCase(requestAccountDeletion.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        if (state.info) state.info.deletion = action.payload;
        state.deleting = false; state.requestId = null;
      })
      .addCase(requestAccountDeletion.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.deleting = false; state.error = action.error.message || "Unable to request deletion."; state.requestId = null;
      });
  },
});

export default privacySlice.reducer;
